import { getBaseURLFromRequest } from '@bridebook/toolbox/src/getBaseURL';
import PageHead from '@bridebook/web/app-basic/components/head/head';
import { basicGSSPWrapper } from '@bridebook/web/app-basic/lib/get-basic-server-side-props-wrapper';
import { fetchWeddingWebsite } from '@bridebook/web/app-basic/lib/wedding-website/api';
import { getWeddingWebsiteOgImageUrl } from '@bridebook/web/app-basic/lib/wedding-website/metadata/get-og-image-url';
import { useWeddingWebsiteMetadata } from '@bridebook/web/app-basic/lib/wedding-website/metadata/use-wedding-website-metadata';
import { GuestView } from '@bridebook/web/components/wedding-website/view-mode/guest-view';
import { GetWeddingWebsiteDataResponse } from '@bridebook/web/pages/api/for/website/[websiteId]';
import { AppType, NextPageWithLayout } from '@bridebook/web/pages/_app';

interface Props {
  initialWeddingWebsiteData: GetWeddingWebsiteDataResponse;
}

const PublicWeddingWebsitePage: NextPageWithLayout<Props> = ({
  initialWeddingWebsiteData,
}: Props) => {
  const partners = initialWeddingWebsiteData.wedding.partners;
  const date = initialWeddingWebsiteData.wedding.date;
  const { websiteTitle, websiteDescription, imageText } = useWeddingWebsiteMetadata({
    partners,
    date,
  });

  const ogImageUrl = getWeddingWebsiteOgImageUrl({
    text: imageText,
    palette: initialWeddingWebsiteData.website.config.theme.palette,
    fontPair: initialWeddingWebsiteData.website.config.theme.font,
    photoPath: initialWeddingWebsiteData.metadataPhotoPath,
    layout: initialWeddingWebsiteData.website.config.layout,
    motif: initialWeddingWebsiteData.motifOgTheme,
  });

  return (
    <PageHead
      title={websiteTitle}
      description={websiteDescription}
      ogTitle={websiteTitle}
      ogDescription={websiteDescription}
      ogImage={ogImageUrl}
      ogType="website"
    />
  );
};

PublicWeddingWebsitePage.getLayout = function getLayout(_page: React.ReactElement<Props>) {
  const { initialWeddingWebsiteData } = _page.props;
  return (
    <>
      {_page}
      <GuestView initialWeddingWebsiteData={initialWeddingWebsiteData} />
    </>
  );
};

export const getServerSideProps = basicGSSPWrapper(
  ['landing', 'weddingWebsite'],
  async ({ context }) => {
    const { websiteId } = context.params as { websiteId: string };

    const baseUrl = getBaseURLFromRequest(context.req);

    try {
      const { data, httpStatus } = await fetchWeddingWebsite(websiteId, {}, baseUrl);
      if (httpStatus === 200 || httpStatus === 401) {
        return {
          props: {
            initialWeddingWebsiteData: data,
          },
        };
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Failed to fetch wedding website server side', e);
      return { notFound: true };
    }

    return { notFound: true };
  },
);

PublicWeddingWebsitePage.AppType = AppType.Basic;

export default PublicWeddingWebsitePage;
